import type { CustomRequest } from '../api/custom-request';
import { hasKey } from './hasKey';

export interface ServerResponse<T = unknown> {
  result_code?: number;
  return_code?: number;
  return_message?: string;
  request?: CustomRequest;
  context?: T;
}

export enum ERROR_CODE {
  HIDDEN = 20019,
  UNKNOWN_BY_CLIENT = 40000,
  UNKNOWN_REQUEST = 40500,
  NO_REQUEST_FUNCTION = 40400,
  NO_ENV = 40401,
  UNKNOWN_UNAUTHORIZED = 40403,
  UNAUTHORIZED_BY_CLIENT = 40404,
}

export const isNormalizedResponse = (res: unknown): res is ServerResponse =>
  hasKey(res, 'result_code') &&
  hasKey(res, 'return_code') &&
  hasKey(res, 'return_message');

export class CustomError extends Error {
  status: number;
  return_code: number;
  return_message: string;
  request?: CustomRequest;

  constructor(params?: ServerResponse) {
    super(
      params?.return_message ??
        '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
    );

    this.return_message = params?.return_message ?? '';
    this.status = params?.result_code ?? 500;
    this.return_code = params?.return_code ?? ERROR_CODE.UNKNOWN_BY_CLIENT;
    this.name = params?.result_code === 200 ? 'Server Error' : 'Client Error';

    const req = params?.request;
    if (req?.endpoint) {
      // Sentry 에러 보고를 위한 추가 컨텍스트 보존 (민감정보 제외)
      this.request = {
        endpoint: req.endpoint,
        method: req.method,
        path: req.path,
        query: req.query,
        statusCode: req.statusCode,
      };
      this.cause = JSON.stringify(this.request);

      // request 맥락이 필요한 케이스만 별도 이슈로 구분
      if (this.return_code === ERROR_CODE.UNKNOWN_REQUEST) {
        this.name = 'Request Error';
      }
    }
  }
}

let errorNum = 0;

//* 서버 요청에 대한 응답을 항상 통일된 형태로 받기 위한 함수
export const normalizeServerRequest = <
  TParams extends unknown[],
  TResponse extends ServerResponse,
>(
  requestFn: (...args: TParams) => Promise<TResponse>,
) => {
  if (!requestFn) {
    errorNum += 1;
    throw new CustomError({
      return_message: `변환된 요청함수가 없습니다. (${errorNum})`,
      result_code: 400,
      return_code: ERROR_CODE.NO_REQUEST_FUNCTION,
    });
  }

  return async (...args: TParams) => {
    try {
      const res: TResponse = await requestFn(...args);

      const { return_code } = res;

      //* result_code가 0인 경우에만 정상적인 응답
      if (return_code === 0) return res;

      if (return_code) throw new CustomError(res);

      throw new CustomError({
        ...res,
        return_message: '잘못된 형식의 응답을 받았습니다.',
      });
    } catch (error) {
      if (error instanceof CustomError) throw error;

      if (error instanceof Error) {
        const errorObject: ServerResponse = {
          return_message: error.message,
          result_code: 500,
          return_code: ERROR_CODE.UNKNOWN_REQUEST,
        };

        const maybeRequest = (error as unknown as { request?: CustomRequest })
          .request;
        if (maybeRequest?.endpoint) {
          // Sentry 에러 보고를 위한 추가 컨텍스트 보존 (민감정보 제외)
          errorObject.request = {
            endpoint: maybeRequest.endpoint,
            method: maybeRequest.method,
            path: maybeRequest.path,
            query: maybeRequest.query,
            statusCode: maybeRequest.statusCode,
          };
        }

        throw new CustomError(errorObject);
      }

      throw new CustomError({
        return_code: ERROR_CODE.UNKNOWN_REQUEST,
      });
    }
  };
};

export const checkServerResponse = async (res: Response) => {
  if (!res.headers.get('content-type')?.includes('application/json')) {
    //* 게이트웨이·프록시가 HTML로 내는 5xx는 JSON 봉투가 아니라 조용히 삼켜졌다 —
    //* 5xx만 명시적으로 에러화해 관측 가능하게 한다 (정상 non-JSON 응답은 통과)
    if (res.status >= 500) {
      await res.body?.cancel();

      throw new CustomError({
        request: { endpoint: res.url },
        return_message: `서버 오류가 발생했습니다. (HTTP ${res.status})`,
        result_code: res.status,
        return_code: ERROR_CODE.UNKNOWN_REQUEST,
      });
    }

    return;
  }

  const clonedRes = res.clone();

  const getRequest = async () => ({
    body: clonedRes.body,
    headers: clonedRes.headers,
    endpoint: clonedRes.url,
  });

  try {
    const data = await clonedRes.json();

    if (typeof data !== 'object' || !isNormalizedResponse(data)) {
      throw new CustomError({
        request: await getRequest(),
        return_message: '잘못된 형식의 응답을 받았습니다.',
        result_code: 500,
        return_code: ERROR_CODE.UNKNOWN_REQUEST,
      });
    }

    if (data.return_code === 0) return;

    throw new CustomError({
      ...data,
      request: await getRequest(),
    });
  } catch (e) {
    // clone()으로 tee된 원본 바디는 미소비 시 undici가 GC까지 버퍼를 보유한다 —
    // 에러로 버려지는 응답은 즉시 해제해 메모리 잔류를 막는다
    await res.body?.cancel();

    if (e instanceof CustomError) throw e;

    if (e instanceof Error) {
      throw new CustomError({
        request: await getRequest(),
        return_message: e.message,
        result_code: 500,
        return_code: ERROR_CODE.UNKNOWN_REQUEST,
      });
    }

    throw new CustomError({
      request: await getRequest(),
      return_code: ERROR_CODE.UNKNOWN_REQUEST,
    });
  }
};
